import { useEffect, useState } from "react";
import { createArena, listArenas } from "../../lib/network";
import type { ArenaMeta, Faction } from "@shared/types";

interface LobbyProps {
  onJoin: (arenaId: string, name: string, faction: Faction) => Promise<void>;
}

export function Lobby({ onJoin }: LobbyProps) {
  const [name, setName] = useState("");
  const [faction, setFaction] = useState<Faction>("dawn");
  const [arenas, setArenas] = useState<ArenaMeta[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState("");

  const refresh = async () => {
    try {
      const { arenas } = await listArenas();
      setArenas(arenas);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, []);

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const { arenaId } = await createArena();
      await onJoin(arenaId, name || "Archer", faction);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create arena");
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onJoin(joinCode.trim(), name || "Archer", faction);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to join arena");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-archer-950 via-archer-900 to-archer-950 p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-black tracking-tight">
            <span className="text-dawn-500">NET</span>
            <span className="text-dusk-500">WORK</span>
            <span className="text-white">ARCHER</span>
          </h1>
          <p className="mt-3 text-archer-300">
            Two factions. One battlefield. Endless arrows.
          </p>
        </div>

        <div className="rounded-2xl border border-archer-700 bg-archer-900/80 p-6 shadow-2xl backdrop-blur">
          <label className="mb-1 block text-sm font-medium text-archer-300">
            Archer Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 20))}
            placeholder="Enter your name..."
            className="mb-4 w-full rounded-lg border border-archer-700 bg-archer-950 px-4 py-3 text-white placeholder-archer-500 outline-none focus:border-dawn-500"
          />

          <label className="mb-1 block text-sm font-medium text-archer-300">
            Choose Faction
          </label>
          <div className="mb-6 grid grid-cols-2 gap-3">
            <button
              onClick={() => setFaction("dawn")}
              className={`rounded-xl border-2 p-4 transition-all ${
                faction === "dawn"
                  ? "border-dawn-500 bg-dawn-500/20 shadow-lg shadow-dawn-500/20"
                  : "border-archer-700 bg-archer-950 hover:border-archer-500"
              }`}
            >
              <div className="text-2xl">☀️</div>
              <div className="mt-1 font-bold text-dawn-400">Dawn Legion</div>
              <div className="text-xs text-archer-300">Golden arrows of the morning</div>
            </button>
            <button
              onClick={() => setFaction("dusk")}
              className={`rounded-xl border-2 p-4 transition-all ${
                faction === "dusk"
                  ? "border-dusk-500 bg-dusk-500/20 shadow-lg shadow-dusk-500/20"
                  : "border-archer-700 bg-archer-950 hover:border-archer-500"
              }`}
            >
              <div className="text-2xl">🌙</div>
              <div className="mt-1 font-bold text-dusk-400">Dusk Horde</div>
              <div className="text-xs text-archer-300">Violet arrows of the night</div>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleCreate}
              disabled={busy}
              className="rounded-xl bg-gradient-to-r from-dawn-600 to-dawn-500 px-6 py-4 font-bold text-white shadow-lg transition-all hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Joining..." : "⚔️ Create & Join Battle"}
            </button>
            <div className="flex gap-2">
              <input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Arena code"
                className="w-full rounded-xl border border-archer-700 bg-archer-950 px-3 py-3 text-sm text-white placeholder-archer-500 outline-none focus:border-dusk-500"
              />
              <button
                onClick={handleJoin}
                disabled={busy || !joinCode.trim()}
                className="rounded-xl bg-gradient-to-r from-dusk-600 to-dusk-500 px-4 py-3 font-bold text-white shadow-lg transition-all hover:brightness-110 disabled:opacity-50"
              >
                Join
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-400">
              {error}
            </div>
          )}

          {arenas.length > 0 && (
            <div className="mt-6">
              <div className="mb-2 text-sm font-medium text-archer-300">
                Open Battles
              </div>
              <div className="space-y-2">
                {arenas.slice(0, 5).map((a) => (
                  <div
                    key={a.arenaId}
                    className="flex items-center justify-between rounded-lg border border-archer-700 bg-archer-950 px-4 py-2"
                  >
                    <div>
                      <span className="font-mono text-dawn-400">{a.arenaId}</span>
                      <span className="ml-3 text-xs text-archer-300">
                        {a.dawnPlayers}☀️ / {a.duskPlayers}🌙
                      </span>
                    </div>
                    <button
                      onClick={() => setJoinCode(a.arenaId)}
                      className="rounded-md bg-archer-700 px-3 py-1 text-xs font-medium hover:bg-archer-500"
                    >
                      Fill code
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-archer-500">
          WASD to move · Click / Space to shoot · E for upgrade shop
        </div>
      </div>
    </div>
  );
}
